import { useTranslation } from 'react-i18next'
import { useMemo, useEffect, useState } from 'react'
import { Select, Modal, Form, Switch } from 'antd'
import PageTable from 'components/PageTable'
import SortableColumnTitle from 'components/SortableColumnTitle'
import Button from 'components/Button'
import { useDispatch, useSelector } from 'react-redux'
import { fetchLovChildrenByParentName } from 'api/lov'
import { getProducts } from 'api/products'
import useClient from 'hooks/useClient'
import { PERMISSIONS } from 'utils/constants'
import { getPlans, createPlan, updatePlan } from 'redux/actions/subscriptions'

const Plans = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isClientCan } = useClient()
  const canCreate = isClientCan(PERMISSIONS.CREATE_SUBSCRIPTION_PLANS)
  const canUpdate = isClientCan(PERMISSIONS.UPDATE_SUBSCRIPTION_PLANS)
  const { plans, isGetLoading, isChangeLoading } = useSelector((state) => state.subscriptions)
  const [durations, setDurations] = useState([])
  const [products, setProducts] = useState([])

  useEffect(() => {
    dispatch(getPlans())
    fetchLovChildrenByParentName('SUBSCRIPTION_PLAN_DURATION').then(setDurations).catch(() => {})
    getProducts(500, 1, true)
      .then((res) => {
        const products = res?.result || res || []
        setProducts(products.filter((product) => product?.category?.name === 'SUBSCRIPTION'))
      })
      .catch(() => {})
  }, [dispatch])

  const columns = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
      {
        title: (p) => <SortableColumnTitle {...p} title='Preferred Plan' id='preferredPlanId' />,
        dataIndex: ['preferredPlan', 'name'],
        key: 'preferredPlanId',
        render: (_, record) => record?.preferredPlan?.descriptionEn || record?.preferredPlan?.name,
      },
      {
        title: 'Product',
        dataIndex: ['product', 'title'],
        key: 'product',
      },
      {
        title: 'Visible',
        dataIndex: 'isVisible',
        key: 'isVisible',
        render: (v) => (v ? 'Yes' : 'No'),
      },
    ],
    [],
  )

  const [form] = Form.useForm()
  const openForm = (record) => {
    form.resetFields()
    if (record) {
      form.setFieldsValue({
        preferredPlanId: record.preferredPlanId || record.preferredPlan?.id,
        productId: record.productId || record.product?.id,
        isVisible: record.isVisible,
      })
    }
    Modal.confirm({
      title: record ? 'Edit Plan' : 'Create Plan',
      icon: null,
      content: (
        <Form form={form} layout='vertical'>
          <Form.Item name='preferredPlanId' label='Preferred Plan' rules={[{ required: true }]}>
            <Select
              options={durations.map((d) => ({ label: d.descriptionEn || d.name, value: d.id }))}
              showSearch
            />
          </Form.Item>
          <Form.Item name='productId' label='Product' rules={[{ required: true }]}>
            <Select
              showSearch
              options={(products || []).map((p) => ({ label: p.title, value: p.id })) || []}
            />
          </Form.Item>
          <Form.Item name='isVisible' label='Visible' valuePropName='checked' initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      ),
      okText: t('app.save'),
      onOk: async () => {
        const values = await form.validateFields()
        if (record) {
          dispatch(updatePlan({ id: record.id, data: values }))
        } else {
          dispatch(createPlan(values))
        }
        form.resetFields()
      },
      onCancel: () => form.resetFields(),
    })
  }

  return (
    <PageTable
      pageTitle='Subscription Plans'
      baseData={plans}
      columns={columns}
      loading={isGetLoading || isChangeLoading}
      action={
        canCreate ? (
          <Button type='primary' onClick={() => openForm(null)}>
            {t('app.add')}
          </Button>
        ) : null
      }
      onRow={
        canUpdate
          ? (record) => ({
              onClick: () => openForm(record),
            })
          : undefined
      }
    />
  )
}

export default Plans
